/**
 * enrollPasskey — the ONE passkey enrolment ceremony (ECOSYSTEM-EMAIL-001 Q-EE-21 unit 1).
 *
 * Fetch the server's registration options → prepare them for the browser → ask the browser for a credential → format
 * it → register it. pos-web's post-login prompt and `usePasskeyManagement` each ran this inline; both now call this.
 * The HTTP legs are the host's (`authService`, an `@puffin-core/auth-client` instance or the console's platform
 * client), the browser legs `WebAuthnService`'s.
 *
 * The options are passed AS ANSWERED: every server mounts `@puffin-core/passkey` `createPasskeyRouter`, whose
 * `/register/options` answers them flat. A browser REJECTS `navigator.credentials.create()` when the person cancels
 * (`NotAllowedError`, also on timeout; `AbortError` when aborted) and when this device already holds one of the
 * excluded credentials (`InvalidStateError`). Those become a coded `PasskeyEnrollmentError`, so a screen words them
 * from the catalog instead of showing the browser's own text. Every other failure — a server refusal with its
 * `errorCode` included — propagates unchanged.
 *
 * @module @puffin-core/mfa-ui/enrollPasskey
 */

/** The ways an enrolment ends without a passkey that a screen words for the person. */
export const PASSKEY_ENROLLMENT_REFUSALS = Object.freeze({
  CANCELLED: 'PASSKEY_ENROLLMENT_CANCELLED',
  ALREADY_REGISTERED: 'PASSKEY_ENROLLMENT_ALREADY_REGISTERED',
});

/** The browser's DOMException names, each mapped to the refusal it means. */
const BROWSER_REFUSALS = Object.freeze({
  NotAllowedError: PASSKEY_ENROLLMENT_REFUSALS.CANCELLED,
  AbortError: PASSKEY_ENROLLMENT_REFUSALS.CANCELLED,
  InvalidStateError: PASSKEY_ENROLLMENT_REFUSALS.ALREADY_REGISTERED,
});

/** An enrolment the person did not finish; `code` is one of `PASSKEY_ENROLLMENT_REFUSALS`. */
export class PasskeyEnrollmentError extends Error {
  /**
   * @param {string} code - a `PASSKEY_ENROLLMENT_REFUSALS` value
   * @param {unknown} [cause] - the browser's rejection, kept for logs
   */
  constructor(code, cause) {
    super(`Passkey enrolment did not finish: ${code}`, cause === undefined ? undefined : { cause });
    this.name = 'PasskeyEnrollmentError';
    this.code = code;
  }
}

/**
 * Ask the browser for a credential, classifying the rejections a person causes.
 *
 * @param {Object} webAuthnService
 * @param {Object} prepared - browser-ready creation options
 * @returns {Promise<Object>} the credential
 */
async function createCredentialOrRefuse(webAuthnService, prepared) {
  let credential;
  try {
    credential = await webAuthnService.createCredential(prepared);
  } catch (err) {
    const code =
      err && Object.hasOwn(BROWSER_REFUSALS, err.name) ? BROWSER_REFUSALS[err.name] : null;
    if (code) throw new PasskeyEnrollmentError(code, err);
    throw err;
  }
  if (!credential) throw new PasskeyEnrollmentError(PASSKEY_ENROLLMENT_REFUSALS.CANCELLED);
  return credential;
}

/**
 * Enrol a passkey for the signed-in person.
 *
 * @param {Object} deps
 * @param {{getPasskeyRegisterOptions: Function, registerPasskey: Function}} deps.authService - the HTTP legs
 * @param {{prepareRegistrationOptions: Function, createCredential: Function, formatRegistrationResponse: Function}}
 *   deps.webAuthnService - the browser legs (`WebAuthnService`)
 * @param {string} [deps.deviceName] - the passkey's friendly name
 * @returns {Promise<Object>} the server's answer to the registration
 * @throws {PasskeyEnrollmentError} when the person cancels, or the device already holds a passkey for this account
 * @throws {TypeError} on an incomplete binding (a programming error)
 */
export async function enrollPasskey({ authService, webAuthnService, deviceName = '' } = {}) {
  if (!authService) throw new TypeError('enrollPasskey: authService is required');
  if (!webAuthnService) throw new TypeError('enrollPasskey: webAuthnService is required');

  const options = await authService.getPasskeyRegisterOptions();
  const prepared = webAuthnService.prepareRegistrationOptions(options);
  const credential = await createCredentialOrRefuse(webAuthnService, prepared);
  const formatted = webAuthnService.formatRegistrationResponse(credential);
  return authService.registerPasskey(formatted, deviceName);
}

export default enrollPasskey;
